import ky, {ResponsePromise} from 'ky';

import {
  AnnotationTelemetryPayload,
  ImageLabelPayloadByModel,
  ImageMeta,
  ImageMetadata,
  ImagesData,
  ImageSortBy,
  ImageSortOrder,
  ImageTypesEnum,
  LabelItem,
  PaginationAction,
  PaginationActionName,
  UndoAnnotationPayload,
  UpdateImagesDataPayload,
  UpdateImagesDataResponse,
  UpdateImagesDataResponseItem,
  UploadImagePayload,
} from '~redux/types/images';

import {getOptions} from './api';
import {processFilters} from './filters.utils';

export interface ImageFiltersPayload {
  // deprecated, do not use these. They are just kept as long as the API still supports them.
  classes?: string[]; // deprecated in favor of humanAnnotationLabels
  classesToExclude?: string[]; // deprecated in favor of excludeHumanAnnotationLabels
  isLabeledByHuman?: boolean; // deprecated in favor of humanAnnotationLabels

  humanAnnotationLabels?: LabelItem['id'][];
  excludeHumanAnnotationLabels?: LabelItem['id'][];
  machinePredictionLabels?: LabelItem['id'][];
  devices?: string[];
  // isLabeledByHuman & isPredictedOnlyByModel are mutually exclusive. If both are set, isLabeledByHuman is prioritized.
  isPredictedOnlyByModel?: boolean; // currently only relevant for live teach-sessions
  withoutMachineDefectPredictions?: boolean;
  withoutMachineObjectPredictions?: boolean;
  labelers?: string[]; // checks all annotations
  latestLabelers?: string[]; // only checks latest annotations
  objectId?: string;
  objectIds?: string[];
  objectIdsToExclude?: string[];
  perspective?: number[];
  probability?: string; // "0.0-1.0" regex: ^(0\.\d+-0\.\d+)|(0\.\d+-1.0)$
  productType?: (string | null)[];
  serialNumber?: string[];
  tags?: string[];
  timeframe?: string;
  trainingTag?: string;
  withNoImages?: boolean;
  includeAnnotationHistory?: boolean;
  sessionTag?: string;
  confusionCell?: Omit<ConfusionCell, 'count' | 'imageIds'> | Omit<ConfusionCell, 'count' | 'imageIds'>[];
  similaritySearch?: SimSearchFilter;
  evaluationCell?: EvaluationCell;
  // Only include images with at least one prediction flagged unstable by enhanced testing. Requires evaluationCell.
  withUnstablePredictions?: boolean;
  meta?: MetadataFiltersPayload;
  metaRange?: Record<string, {min: number; max: number}>;
  automlPipelineId?: string;
  modelId?: string;
  humanPolygonAreaSize?: {minArea: number; maxArea: number};
  keypointFinetune?: KeypointFinetune;
  keypointEvaluation?: KeypointEvaluation;
  keypointSelectedDistance?: string[];
  onlyDefectPredictions?: boolean;
  version?: string;
  curationPipelineTag?: string;
  widthPx?: {min: number | null; max: number | null};
  heightPx?: {min: number | null; max: number | null};
}

// This type is extracted, because we require it for caching the images data
export interface SortAndCountArgs {
  sortBy: ImageSortBy | undefined; // default: TIMESTAMP
  sortOrder: ImageSortOrder | undefined; // default: DESC
  count: number | undefined; // 1 - 10.000
}

export interface PaginationPayload extends Partial<SortAndCountArgs> {
  action?: PaginationAction;
}

export interface ImagesDataPayload {
  pagination?: PaginationPayload;
  filters?: ImageFiltersPayload;
}

const AUTH_API = import.meta.env.VITE_AUTH_API ?? '';
const imagesEndpoint = `${AUTH_API}/images`;
let lastAnnotatedImage: {projectId: string; imageId: string} | null = null;

const withJumpToLastAnnotatedImage = (projectId: string, pagination?: PaginationPayload) => {
  const action = pagination?.action;
  if (
    lastAnnotatedImage?.projectId !== projectId ||
    pagination?.sortBy !== 'ANNOTATION_TIMESTAMP' ||
    pagination.sortOrder === 'ASC' ||
    action?.name !== PaginationActionName.JUMP ||
    action.pageOffset
  ) {
    return pagination;
  }
  return {...pagination, action: {...action, cursor: lastAnnotatedImage.imageId}};
};

export const getImagesData = (
  projectId: string,
  payload: ImagesDataPayload,
  reactQuerySignal: AbortSignal | undefined,
  metadata?: MetadataFilters,
): Promise<ImagesData> => {
  const {filters, pagination} = payload;

  return ky
    .post(
      `${imagesEndpoint}/${projectId}`,
      getOptions<ImagesDataPayload>({
        apiVersion: 3,
        signalId: `getImagesData-${projectId}`,
        reactQuerySignal,
        data: {
          filters: processFilters(filters, metadata),
          pagination: withJumpToLastAnnotatedImage(projectId, pagination),
        },
      }),
    )
    .json<ImagesData>()
    .then((response) => {
      lastAnnotatedImage = null;
      return response;
    });
};

export const putImagesData = (
  projectID: string,
  payload: UpdateImagesDataPayload,
  options: {skipCursor?: boolean} = {},
) => {
  const imageId = payload[0]?.imageId;
  if (!options.skipCursor && imageId) {
    lastAnnotatedImage = {projectId: projectID, imageId};
  }
  return ky
    .put(
      `${imagesEndpoint}/${projectID}`,
      getOptions({
        apiVersion: 3,
        signalId: 'putImagesData',
        data: payload,
      }),
    )
    .json<UpdateImagesDataResponse>();
};

export const undoLastAnnotation = (projectID: string, payload: UndoAnnotationPayload) => {
  return ky
    .post(
      `${imagesEndpoint}/undo/${projectID}`,
      getOptions<UndoAnnotationPayload>({
        apiVersion: 3,
        signalId: 'undoLastAnnotation',
        data: payload,
      }),
    )
    .json<UpdateImagesDataResponseItem>();
};

export const deleteImages = (projectId: string, itemIDs: string[]): ResponsePromise => {
  // Note: all image data objects have IDs but not all have keys (the ones without an image).
  // The handling in the backend is the same for the keys and ids array, so there is no reason
  // to separate between keys and IDs here. Therefor IDs only IDs are used for consistency.
  const data = {ids: itemIDs};
  return ky.put(`${imagesEndpoint}/${projectId}/delete`, getOptions({apiVersion: 3, signalId: 'deleteImages', data}));
};

type imageUploadPayload = imageUploadPayloadItem[];
interface imageUploadPayloadItem {
  date?: number;
  fileId: string;
  fileType: ImageTypesEnum;
  label?: string;
}

export const generateImageUploadUrls = (projectId: string, imageFiles: imageUploadPayload) => {
  return ky
    .post(
      `${imagesEndpoint}/${projectId}/upload_urls`,
      getOptions({
        apiVersion: 3,
        signalId: 'imageUploadUrls',
        data: {imageFiles: imageFiles},
      }),
    )
    .json<any>();
};

export const uploadImagesToBlobStorage = (urlPresigned: string, file: Blob | Uint8Array | ArrayBuffer) => {
  const body =
    file instanceof Blob ? file : file instanceof ArrayBuffer ? new Blob([file]) : new Blob([new Uint8Array(file)]);

  return ky.put(
    urlPresigned,
    getOptions({
      apiVersion: 3,
      signalId: 'uploadImagesToBlobStorage',
      body,
    }),
  );
};

export const uploadImagesUrls = (projectId: string, payload: UploadImagePayload) => {
  return ky
    .post(
      `${imagesEndpoint}/${projectId}/upload`,
      getOptions({
        apiVersion: 3,
        signalId: 'imageUploadUrls',
        data: payload,
        customHeaders: {
          'x-ms-blob-type': 'BlockBlob',
        },
      }),
    )
    .json<any>();
};

interface TagImagesPayload {
  ids: string[];
  tags: string[];
}

export const tagImages = (projectId: string, payload: TagImagesPayload): ResponsePromise => {
  return ky.put(
    `${imagesEndpoint}/${projectId}/tag`,
    getOptions({apiVersion: 3, signalId: 'tagImages', data: payload}),
  );
};

interface UntagImagesPayload {
  ids: string[];
  tags: string[];
}

export const untagImages = (projectId: string, payload: UntagImagesPayload): ResponsePromise => {
  return ky.put(
    `${imagesEndpoint}/${projectId}/untag`,
    getOptions({apiVersion: 3, signalId: 'untagImages', data: payload}),
  );
};

export const updateMeta = (projectId: string, imagesMeta: ImageMeta[]): ResponsePromise => {
  return ky.put(
    `${imagesEndpoint}/${projectId}/meta`,
    getOptions({apiVersion: 3, signalId: 'setMeta', data: imagesMeta}),
  );
};

interface BulkTagImagesPayload {
  filters?: ImageFiltersPayload;
  tags: string[];
}

interface BulkMetaImagesPayload {
  filters?: ImageFiltersPayload;
  meta: ImageMetadata;
}

interface bulkClassifyImagesPayload {
  filters?: ImageFiltersPayload;
  labelsByModel: ImageLabelPayloadByModel;
  uiContext: string;
  telemetry?: AnnotationTelemetryPayload;
}

export const bulkTagImages = (
  projectId: string,
  payload: BulkTagImagesPayload,
  metadata?: MetadataFilters,
): ResponsePromise => {
  return ky.put(
    `${imagesEndpoint}/${projectId}/bulk-tag`,
    getOptions({
      apiVersion: 3,
      signalId: 'bulkTagImages',
      data: {
        ...payload,
        filters: processFilters(payload.filters, metadata),
      },
    }),
  );
};

export const bulkUntagImages = (
  projectId: string,
  payload: BulkTagImagesPayload,
  metadata?: MetadataFilters,
): ResponsePromise => {
  return ky.put(
    `${imagesEndpoint}/${projectId}/bulk-untag`,
    getOptions({
      apiVersion: 3,
      signalId: 'bulkUntagImages',
      data: {
        ...payload,
        filters: processFilters(payload.filters, metadata),
      },
    }),
  );
};

export const bulkUpdateMeta = (
  projectId: string,
  payload: BulkMetaImagesPayload,
  metadata?: MetadataFilters,
): ResponsePromise => {
  return ky.put(
    `${imagesEndpoint}/${projectId}/bulk_update_meta_raw_case`,
    getOptions({
      apiVersion: 3,
      signalId: 'bulkSetMeta',
      data: {
        ...payload,
        filters: processFilters(payload.filters, metadata),
      },
    }),
  );
};

export const bulkAssignClass = (
  projectId: string,
  payload: bulkClassifyImagesPayload,
  metadata?: MetadataFilters,
): ResponsePromise => {
  return ky.put(
    `${imagesEndpoint}/bulk-update/${projectId}`,
    getOptions({
      apiVersion: 3,
      signalId: 'bulkAssignClass',
      data: {
        ...payload,
        filters: processFilters(payload.filters, metadata),
      },
    }),
  );
};

export const getImagesIds = (
  projectId: string,
  filters: ImagesDataPayload['filters'],
  pagination: ImagesDataPayload['pagination'],
  reactQuerySignal: AbortSignal | undefined,
  metadata?: MetadataFilters,
): Promise<{imageIds: string[]}> => {
  return ky
    .post(
      `${imagesEndpoint}/${projectId}/bulk_ids`,
      getOptions<ImagesDataPayload>({
        apiVersion: 3,
        signalId: `getImagesData-${projectId}`,
        reactQuerySignal,
        data: {
          filters: processFilters(filters, metadata),
          pagination,
        },
      }),
    )
    .json<{imageIds: string[]}>();
};

export const getGroupedImagesData = (
  projectId: string,
  payload: ImagesDataPayload,
  reactQuerySignal?: AbortSignal,
  metadata?: MetadataFilters,
): Promise<GroupedImagesData> => {
  const {filters, ...restPayload} = payload;

  return ky
    .post(
      `${AUTH_API}/grouped_images/${projectId}`,
      getOptions<ImagesDataPayload>({
        apiVersion: 3,
        signalId: `getGroupedImagesData-${projectId}`,
        reactQuerySignal,
        data: {
          ...restPayload,
          filters: processFilters(filters, metadata),
        },
      }),
    )
    .json<GroupedImagesData>();
};

export interface GeneratePolygonPayload {
  image_id: string;
  boundingBox: {
    min_x: number;
    min_y: number;
    max_x: number;
    max_y: number;
  };
}

export interface GeneratePolygonResponse {
  coordinates: Array<{x: number; y: number}>;
}

export const generatePolygon = (
  projectId: string,
  payload: GeneratePolygonPayload,
): Promise<GeneratePolygonResponse> => {
  return ky
    .post(
      `${imagesEndpoint}/${projectId}/generate_polygon`,
      getOptions({apiVersion: 3, signalId: 'generatePolygon', data: payload}),
    )
    .json<GeneratePolygonResponse>();
};
